import React, { useState, useContext, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Link,
  Alert,
  InputAdornment,
  IconButton,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { AuthContext } from "../../context/AuthContext";
import { getPrivateRouteByRole, hasAceptadoTratamientoDatos } from "../../utils/authUser";
import {
  clearStoredSession,
  isTokenValid,
} from "../../utils/session";
import Swal from "sweetalert2";

const Login = () => {
  const [correo, setCorreo] = useState("");
  const [contraseña, setContraseña] = useState("");
  const [recordarme, setRecordarme] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { login } = useContext(AuthContext);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const rawUser = localStorage.getItem("user");

    if (token && !isTokenValid(token)) {
      clearStoredSession();
    }

    if (token && rawUser && isTokenValid(token)) {
      try {
        const user = JSON.parse(rawUser);
        if (hasAceptadoTratamientoDatos(user)) {
          window.location.replace(getPrivateRouteByRole(user?.idRol));
        }
        return;
      } catch (e) {
        clearStoredSession();
      }
    }

    const remembered = localStorage.getItem("loginRememberMe") === "1";
    if (!remembered) return;

    setRecordarme(true);
    setCorreo(localStorage.getItem("loginCorreo") || "");
    setContraseña(localStorage.getItem("loginPassword") || "");
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      if (recordarme) {
        localStorage.setItem("loginRememberMe", "1");
        localStorage.setItem("loginCorreo", correo);
        localStorage.setItem("loginPassword", contraseña);
      } else {
        localStorage.removeItem("loginRememberMe");
        localStorage.removeItem("loginCorreo");
        localStorage.removeItem("loginPassword");
      }
      await login(correo, contraseña);
    } catch (err) {
      setError("Error al iniciar sesión. Verifica tus credenciales.");
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Error al iniciar sesión. Verifica tus credenciales.",
        confirmButtonColor: "#a5b967",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
        background: "#a5b967",
      }}
    >
      <Box
        component={Paper}
        elevation={3}
        sx={{
          padding: 4,
          maxWidth: 400,
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          borderRadius: 2,
          backgroundColor: "white",
        }}
      >
        <Typography variant="h5" fontWeight="bold" gutterBottom align="center">
          Iniciar sesión en BioPlastic
        </Typography>
        <Typography
          variant="body1"
          sx={{ marginBottom: 2, color: "#6e6e6e", textAlign: "center" }}
        >
          Ingresa tus credenciales para continuar
        </Typography>

        {error && (
          <Alert severity="error" sx={{ width: "100%", mb: 2 }}>
            {error}
          </Alert>
        )}

        <TextField
          fullWidth
          label="Email"
          type="email"
          variant="outlined"
          value={correo}
          onChange={(e) => setCorreo(e.target.value)}
          size="medium"
          sx={{ mb: 2 }}
        />

        <TextField
          fullWidth
          label="Contraseña"
          type={showPassword ? "text" : "password"}
          variant="outlined"
          value={contraseña}
          onChange={(e) => setContraseña(e.target.value)}
          size="medium"
          sx={{ mb: 2 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={recordarme}
              onChange={(e) => setRecordarme(e.target.checked)}
              sx={{ color: "#a5b967", "&.Mui-checked": { color: "#a5b967" } }}
            />
          }
          label="Recordarme"
          sx={{ width: "100%", mb: 1 }}
        />

        <Button
          variant="contained"
          fullWidth
          sx={{
            mt: 1,
            backgroundColor: "#a5b967",
            "&:hover": { backgroundColor: "#8da253" },
          }}
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? "Cargando..." : "Ingresar"}
        </Button>

        <Box sx={{ mt: 3, textAlign: "center" }}>
          <Link
            href="/Remember"
            variant="body2"
            sx={{
              display: "block",
              mb: 1,
              color: "#a5b967",
              textDecoration: "none",
              "&:hover": { color: "#8da253" },
            }}
          >
            ¿Has olvidado tu contraseña?
          </Link>
          <Link
            href="/Register"
            variant="body2"
            sx={{
              color: "#a5b967",
              textDecoration: "none",
              "&:hover": { color: "#8da253" },
            }}
          >
            ¿No tienes cuenta? Regístrate
          </Link>
        </Box>
      </Box>
    </Box>
  );
};

export default Login;
